import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { PerplexityStockAnalysisData } from '../../types';

interface PerplexityAnalysisState {
  byTicker: Record<string, PerplexityStockAnalysisData & {
    success: boolean;
    error?: string;
    is_cached?: boolean;
    analyzed_at?: string;
    cache_age_hours?: number;
  }>;
  loading: boolean;
  error: string | null;
  lastUpdated: Record<string, number>; // Timestamp per ticker
}

// LocalStorage key for persistence
const STORAGE_KEY = 'perplexityAnalysis';
const CACHE_TTL_HOURS = 24;

// Load state from localStorage with TTL validation
const loadFromLocalStorage = (): Partial<PerplexityAnalysisState> => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return {};

    const parsed = JSON.parse(stored);
    const now = Date.now();
    const ttlMs = CACHE_TTL_HOURS * 60 * 60 * 1000;

    // Filter out stale data (> 24h old)
    const filteredByTicker: typeof parsed.byTicker = {};
    const filteredLastUpdated: typeof parsed.lastUpdated = {};

    Object.entries(parsed.byTicker || {}).forEach(([ticker, data]: [string, any]) => {
      const analyzedAt = data.analyzed_at ? new Date(data.analyzed_at).getTime() : 0;
      const age = now - analyzedAt;

      if (age < ttlMs) {
        filteredByTicker[ticker] = data;
        if (parsed.lastUpdated?.[ticker]) {
          filteredLastUpdated[ticker] = parsed.lastUpdated[ticker];
        }
      }
    });

    return {
      byTicker: filteredByTicker,
      lastUpdated: filteredLastUpdated,
    };
  } catch (error) {
    console.error('Failed to load Perplexity analysis from localStorage:', error);
    return {};
  }
};

// Save state to localStorage
const saveToLocalStorage = (state: PerplexityAnalysisState) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      byTicker: state.byTicker,
      lastUpdated: state.lastUpdated,
    }));
  } catch (error) {
    console.error('Failed to save Perplexity analysis to localStorage:', error);
  }
};

const initialState: PerplexityAnalysisState = {
  byTicker: {},
  loading: false,
  error: null,
  lastUpdated: {},
  ...loadFromLocalStorage(), // Load persisted data
};

const perplexityAnalysisSlice = createSlice({
  name: 'perplexityAnalysis',
  initialState,
  reducers: {
    setAnalysisData: (
      state,
      action: PayloadAction<PerplexityStockAnalysisData & {
        success: boolean;
        error?: string;
        is_cached?: boolean;
        analyzed_at?: string;
        cache_age_hours?: number;
      }>
    ) => {
      const ticker = action.payload.ticker.toUpperCase();
      state.byTicker[ticker] = action.payload;
      // Only update lastUpdated timestamp for fresh (non-cached) data
      if (!action.payload.is_cached) {
        state.lastUpdated[ticker] = Date.now();
      }
      state.loading = false;
      state.error = null;
      saveToLocalStorage(state); // Persist to localStorage
    },
    setAnalysisLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
      if (action.payload) {
        state.error = null;
      }
    },
    setAnalysisError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.loading = false;
    },
    clearAnalysisData: (state, action: PayloadAction<string>) => {
      const ticker = action.payload.toUpperCase();
      delete state.byTicker[ticker];
      delete state.lastUpdated[ticker];
      saveToLocalStorage(state); // Persist to localStorage
    },
    clearAllAnalysisData: (state) => {
      state.byTicker = {};
      state.lastUpdated = {};
      state.error = null;
      saveToLocalStorage(state); // Persist to localStorage
    },
  },
});

export const {
  setAnalysisData,
  setAnalysisLoading,
  setAnalysisError,
  clearAnalysisData,
  clearAllAnalysisData,
} = perplexityAnalysisSlice.actions;

export default perplexityAnalysisSlice.reducer;
